import React, { useEffect, useState } from "react";
import { Building2, Check, ChevronsUpDown, User as UserIcon } from "lucide-react";
import api from "@/lib/api";
import { cn } from "@/lib/utils";
import { useLang } from "@/i18n";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const ROLE_LABELS = { OWNER: "Propriétaire", ADMIN: "Administrateur", MEMBER: "Membre" };

export const WorkspaceSwitcher = ({ collapsed = false }) => {
  const { tf } = useLang();
  const [data, setData] = useState(null);
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    api.get("/workspaces").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const workspaces = data?.workspaces || [];
  if (workspaces.length < 2) return null;
  const active = workspaces.find((w) => w.is_active) || workspaces[0];

  const switchTo = async (ws) => {
    if (ws.is_active || switching) return;
    setSwitching(true);
    try {
      await api.post("/workspaces/switch", { workspace_id: ws.workspace_id });
      window.location.href = "/dashboard";
    } catch (e) {
      setSwitching(false);
      toast.error(tf("Changement de workspace impossible"));
    }
  };

  return (
    <div className={cn("pb-3", collapsed ? "px-2" : "px-3")}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid="workspace-switcher-trigger"
            className={cn(
              "w-full flex items-center gap-2 rounded-lg border border-border bg-secondary/40 hover:bg-secondary/70 transition-colors text-left",
              collapsed ? "justify-center h-9 w-9 mx-auto" : "px-2.5 py-2"
            )}
          >
            {active.is_personal
              ? <UserIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              : <Building2 className="h-3.5 w-3.5 shrink-0 text-athlynx" />}
            {!collapsed && (
              <>
                <span className="flex-1 min-w-0">
                  <span className="block text-xs font-semibold truncate">{active.name}</span>
                  <span className="block text-[10px] text-muted-foreground truncate">
                    {active.is_personal ? tf("Espace personnel") : tf(ROLE_LABELS[active.role] || active.role)}
                  </span>
                </span>
                <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground/60 shrink-0" />
              </>
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-60">
          <DropdownMenuLabel className="text-xs">{tf("Changer de workspace")}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {workspaces.map((w) => (
            <DropdownMenuItem
              key={w.workspace_id}
              data-testid={`workspace-switch-${w.workspace_id}`}
              onClick={() => switchTo(w)}
              className="gap-2 cursor-pointer"
            >
              {w.is_personal
                ? <UserIcon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                : <Building2 className="h-3.5 w-3.5 text-athlynx shrink-0" />}
              <span className="flex-1 min-w-0">
                <span className="block text-xs font-medium truncate">{w.name}</span>
                <span className="block text-[10px] text-muted-foreground">
                  {w.is_personal ? tf("Espace personnel") : tf(ROLE_LABELS[w.role] || w.role)}
                </span>
              </span>
              {w.is_active && <Check className="h-3.5 w-3.5 text-emerald-400 shrink-0" />}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
